import React from 'react';
import { Copy, ExternalLink, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './Button';
import { copyToClipboard } from '../../utils/clipboard';

interface LinkShareBlockProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  link: string | null;
  onGenerate: () => void;
  generating?: boolean;
  badgeLabel?: string;
  warningMessage?: string;
  generateLabel?: string;
  openLabel?: string;
  showOpenPdf?: boolean;
}

export function LinkShareBlock({
  icon, title, description, link, onGenerate, generating,
  badgeLabel, warningMessage, generateLabel = 'Genera link',
  openLabel = 'Apri', showOpenPdf = false,
}: LinkShareBlockProps) {
  const copyLink = () => {
    if (!link) return;
    copyToClipboard(link).then(ok =>
      toast.success(ok ? 'Link copiato' : 'Link generato')
    );
  };

  return (
    <div style={{
      background: 'var(--glass-bg-card)',
      border: `1px solid ${badgeLabel ? 'var(--color-success)' : 'rgba(255,255,255,0.10)'}`,
      borderRadius: 'var(--radius-card)',
      padding: '1.25rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.875rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {icon}
          <p style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>{title}</p>
        </div>
        {badgeLabel && (
          <span style={{
            fontSize: 'var(--text-xs)', fontWeight: 600,
            background: 'var(--color-surface-3)', color: 'var(--color-text-muted)',
            borderRadius: 'var(--radius-interactive)', padding: '0.125rem 0.5rem',
          }}>
            {badgeLabel}
          </span>
        )}
      </div>

      {warningMessage && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
          background: 'var(--color-warning-highlight, #fffbeb)',
          border: '1px solid var(--color-warning, #f59e0b)',
          borderRadius: 'var(--radius-card)', padding: '0.5rem 0.75rem',
        }}>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
            {warningMessage}
          </p>
        </div>
      )}

      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
        {description}
      </p>

      {link ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-card)', padding: '0.5rem 0.75rem',
          }}>
            <span style={{ flex: 1, fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {link}
            </span>
            <button onClick={copyLink} title="Copia link"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              <Copy size={14} />
            </button>
            <a href={link} target="_blank" rel="noreferrer" title={openLabel}
              style={{ color: 'var(--color-primary)', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              <ExternalLink size={14} />
            </a>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button size="sm" variant="secondary" icon={<Copy size={13} />} onClick={copyLink}>
              Copia link
            </Button>
            {showOpenPdf && (
              <Button size="sm" variant="secondary" icon={<ExternalLink size={13} />}
                onClick={() => window.open(link!, '_blank')}>
                Apri PDF
              </Button>
            )}
            <Button size="sm" variant="ghost" icon={<RotateCcw size={13} />}
              onClick={onGenerate} loading={generating}>
              Rigenera
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="secondary" icon={icon} onClick={onGenerate} loading={generating}>
          {generateLabel}
        </Button>
      )}
    </div>
  );
}
